import React from 'react';
import { createPortal } from 'react-dom';
import { Checkbox, IconButton, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Draggable } from '@hello-pangea/dnd';
import { isColumnReorderLocked } from '@/components/table/columnOrder';

// Some columns use a blank headerName to hide the grid header; fall back to the field
const getColumnLabel = (column) => column.headerName?.trim() || column.field;

const ColumnReorderListItem = ({
	column,
	index,
	visible,
	canMoveUp,
	canMoveDown,
	onToggleVisibility,
	onMoveUp,
	onMoveDown,
}) => {
	const hideable = column.hideable !== false;
	const reorderLocked = isColumnReorderLocked(column);
	const label = getColumnLabel(column);

	return (
		<Draggable draggableId={column.field} index={index} isDragDisabled={reorderLocked}>
			{(draggableProvided, snapshot) => {
				const row = (
					<ListItem
						ref={draggableProvided.innerRef}
						{...draggableProvided.draggableProps}
						disablePadding
						{...(snapshot.isDragging && { sx: { bgcolor: 'action.selected' } })}
					>
						<ListItemIcon
							{...draggableProvided.dragHandleProps}
							sx={{
								minWidth: 'auto',
								...(reorderLocked && {
									cursor: 'default',
									color: 'action.disabled',
								}),
							}}
						>
							<DragIndicatorIcon fontSize='small' />
						</ListItemIcon>
						<ListItemIcon sx={{ minWidth: 'auto' }}>
							<Checkbox
								size='small'
								checked={visible}
								disabled={!hideable}
								onChange={onToggleVisibility}
								slotProps={{ input: { 'aria-label': `Show ${label}` } }}
							/>
						</ListItemIcon>
						<ListItemText
							primary={label}
							slotProps={{
								primary: {
									noWrap: true,
									...(reorderLocked && { color: 'text.disabled' }),
								},
							}}
						/>
						<IconButton
							size='small'
							disabled={!canMoveUp}
							onMouseDown={(event) => event.preventDefault()}
							onClick={onMoveUp}
							aria-label={`Move ${label} up`}
						>
							<KeyboardArrowUpIcon fontSize='small' />
						</IconButton>
						<IconButton
							size='small'
							disabled={!canMoveDown}
							onMouseDown={(event) => event.preventDefault()}
							onClick={onMoveDown}
							aria-label={`Move ${label} down`}
						>
							<KeyboardArrowDownIcon fontSize='small' />
						</IconButton>
					</ListItem>
				);

				// Popover uses transform; portal keeps the drag preview positioned correctly.
				return snapshot.isDragging ? createPortal(row, document.body) : row;
			}}
		</Draggable>
	);
};

export default ColumnReorderListItem;
